/**
 * Iter60.p40 — Compact "Split by projects" dialog.
 *
 * Purpose: give operators a focused UI that ONLY exposes the multi-project
 * time allocation table. The full `EditAttendanceDialog` (Status / Late /
 * OT / Multiplier / Notes / Excused / Adjustments + tabs / audit history)
 * remains the tool for the "Edit attendance…" menu item.
 *
 * Backend contract is identical to the full modal — this dialog hits
 * exactly the same endpoint (`PATCH /attendance/daily/{id}`) with exactly
 * the same body shape (`{ project_allocations: [{project_id, minutes, note}] }`)
 * so no server changes are required. See `EditAttendanceDialog.buildPatch()`.
 *
 * Availability: IC + CN only. AP does not support multi-project splitting
 * (already gated in `Attendance.jsx::ReclassifyMenu.showSplitProjects`).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

import { api, formatApiError } from "../lib/api";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import ProjectPicker from "./ProjectPicker";
import ModuleIdChip from "./ModuleIdChip";


function fmtHM(min) {
    const m = Math.max(0, Number(min) || 0);
    const h = Math.floor(m / 60);
    const r = m % 60;
    return `${h}h ${String(r).padStart(2, "0")}m`;
}


export default function SplitByProjectsDialog({
    open, onOpenChange, row, moduleCode, onSaved,
}) {
    const [projects, setProjects] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [origJson, setOrigJson] = useState("[]");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !row) return;
        // Seed allocations from the row snapshot (same shape the full
        // modal uses so we can hit the identical endpoint contract).
        const initAllocs = Array.isArray(row.project_allocations)
            ? row.project_allocations.map((a) => ({
                project_id: a.project_id || "",
                minutes: Number(a.minutes) || 0,
                note: a.note || "",
            }))
            : [];
        setAllocations(initAllocs);
        setOrigJson(JSON.stringify(initAllocs));
        // Load projects scoped to the row's module.
        api.get("/projects", { params: { module_id: row.module_id } })
            .then(({ data }) => setProjects(Array.isArray(data) ? data : (data?.items || [])))
            .catch(() => setProjects([]));
    }, [open, row]);

    if (!row) return null;

    const worked = Number(row.worked_minutes) || 0;
    const allocSum = allocations.reduce(
        (s, a) => s + (Number(a.minutes) || 0), 0,
    );
    const remaining = worked - allocSum;
    const over = allocSum > worked;

    const activeProjects = (projects || []).filter((p) => p.active !== false);
    const setAt = (idx, patch) => {
        setAllocations((cur) => cur.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    };
    const remove = (idx) => {
        setAllocations((cur) => cur.filter((_, i) => i !== idx));
    };
    const add = () => {
        setAllocations((cur) => [...cur, { project_id: "", minutes: 0, note: "" }]);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        // Match the full modal's cleanup / diff so the endpoint sees an
        // identical payload shape. Filter incomplete rows, coerce
        // minutes to non-negative integers, trim notes.
        const cleaned = allocations
            .filter((a) => a.project_id && Number(a.minutes) > 0)
            .map((a) => ({
                project_id: a.project_id,
                minutes: Math.max(0, Math.round(Number(a.minutes) || 0)),
                note: (a.note || "").trim() || null,
            }));
        const nextJson = JSON.stringify(cleaned);
        if (nextJson === origJson) {
            toast.info("No changes to save");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch(
                `/attendance/daily/${row.id}`,
                { project_allocations: cleaned },
            );
            toast.success("Project split saved");
            onOpenChange(false);
            onSaved && onSaved(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const empName = row.employee_name || row.full_name || row.employee?.full_name || "—";
    const empModuleId = row.module_employee_id || row.employee?.module_employee_id || "";
    const modCode = (moduleCode || row.module_code || "").toUpperCase();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="split-by-projects-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        Split by projects
                        {modCode && (
                            <span
                                className="inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-wider rounded asl-navy-bg text-white"
                                data-testid="split-projects-module-chip"
                            >
                                {modCode}
                            </span>
                        )}
                    </DialogTitle>
                    <div
                        className="text-xs text-[color:var(--asl-muted)] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1"
                        data-testid="split-projects-header-meta"
                    >
                        {/* Iter60.p52 — swap raw `(EMP-####)` fallback text
                            for the crimson Module ID pill so it matches the
                            Attendance grid + Payroll table. Falls back to a
                            muted `—` chip when the employee has no Module ID. */}
                        <ModuleIdChip
                            value={empModuleId}
                            testid="split-projects-header-module-id"
                        />
                        <span className="font-medium asl-navy-text">
                            {empName}
                        </span>
                        <span>{row.work_date}</span>
                    </div>
                </DialogHeader>

                <form onSubmit={onSubmit} className="space-y-3">
                    <div
                        className="rounded-md border border-[color:var(--asl-border)] bg-white p-2 flex flex-wrap items-baseline gap-x-4 text-xs"
                        data-testid="split-projects-summary"
                    >
                        <span>
                            <span className="text-[color:var(--asl-muted)]">Worked </span>
                            <span
                                className="mono font-semibold asl-navy-text"
                                data-testid="split-projects-summary-worked"
                            >
                                {fmtHM(worked)}
                            </span>
                        </span>
                        <span>
                            <span className="text-[color:var(--asl-muted)]">Allocated </span>
                            <span
                                className={`mono font-semibold ${over ? "text-[color:var(--asl-crimson)]" : "asl-navy-text"}`}
                                data-testid="split-projects-summary-allocated"
                            >
                                {fmtHM(allocSum)}
                            </span>
                        </span>
                        <span>
                            <span className="text-[color:var(--asl-muted)]">Remaining </span>
                            <span
                                className={`mono font-semibold ${remaining < 0 ? "text-[color:var(--asl-crimson)]" : "asl-navy-text"}`}
                                data-testid="split-projects-summary-remaining"
                            >
                                {fmtHM(Math.max(0, remaining))}
                            </span>
                        </span>
                    </div>

                    {over && (
                        <div
                            className="rounded-md border bg-rose-50 border-rose-200 p-2 text-xs text-rose-800 flex items-start gap-2"
                            data-testid="split-projects-over-warning"
                        >
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                            Allocated minutes exceed worked_minutes. The server will reject this on save.
                        </div>
                    )}

                    {allocations.length === 0 ? (
                        <div
                            className="text-xs text-[color:var(--asl-muted)] italic py-6 text-center border border-dashed border-[color:var(--asl-border)] rounded-md"
                            data-testid="split-projects-empty"
                        >
                            No project splits yet — the full worked total falls into
                            <span className="mono"> "— No project —"</span> in Cost by Project.
                        </div>
                    ) : (
                        <table
                            className="w-full text-xs"
                            data-testid="split-projects-table"
                        >
                            <thead className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                <tr>
                                    <th className="text-left px-1 py-1">Project</th>
                                    <th className="text-right px-1 py-1 w-16">Hours</th>
                                    <th className="text-right px-1 py-1 w-16">Minutes</th>
                                    <th className="text-left px-1 py-1">Note</th>
                                    <th className="w-6"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocations.map((a, idx) => {
                                    const hours = Math.floor((Number(a.minutes) || 0) / 60);
                                    const mins = (Number(a.minutes) || 0) % 60;
                                    const onHours = (v) => {
                                        const h = Math.max(0, Math.floor(Number(v) || 0));
                                        setAt(idx, { minutes: h * 60 + mins });
                                    };
                                    const onMins = (v) => {
                                        const m = Math.max(0, Math.min(59, Math.floor(Number(v) || 0)));
                                        setAt(idx, { minutes: hours * 60 + m });
                                    };
                                    return (
                                        <tr
                                            key={idx}
                                            className="border-t"
                                            style={{ borderColor: "var(--asl-border)" }}
                                            data-testid={`split-projects-row-${idx}`}
                                        >
                                            <td className="px-1 py-1">
                                                {/* Iter60.p49.1 — Job # prefix + searchable dropdown.
                                                    Label: `{code} · {name}` (falls back to name
                                                    when code missing). Search matches BOTH. */}
                                                <ProjectPicker
                                                    projects={activeProjects}
                                                    value={a.project_id}
                                                    onValueChange={(v) => setAt(idx, { project_id: v })}
                                                    testid={`split-projects-pid-${idx}`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number" min="0" step="1"
                                                    className="h-8"
                                                    value={hours}
                                                    onChange={(e) => onHours(e.target.value)}
                                                    data-testid={`split-projects-hours-${idx}`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    type="number" min="0" max="59" step="1"
                                                    className="h-8"
                                                    value={mins}
                                                    onChange={(e) => onMins(e.target.value)}
                                                    data-testid={`split-projects-mins-${idx}`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <Input
                                                    className="h-8"
                                                    placeholder="optional"
                                                    value={a.note || ""}
                                                    onChange={(e) => setAt(idx, { note: e.target.value })}
                                                    data-testid={`split-projects-note-${idx}`}
                                                />
                                            </td>
                                            <td className="px-1 py-1">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(idx)}
                                                    className="text-xs text-[color:var(--asl-crimson)] hover:underline"
                                                    data-testid={`split-projects-remove-${idx}`}
                                                >
                                                    ✕
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}

                    <button
                        type="button"
                        onClick={add}
                        className="text-sm text-[color:var(--asl-crimson)] font-medium hover:underline"
                        data-testid="split-projects-add-btn"
                    >
                        + Add project
                    </button>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                            data-testid="split-projects-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="asl-crimson-bg text-white"
                            disabled={saving || over}
                            data-testid="split-projects-save-btn"
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
